'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { DualInputSlider } from './DualInputSlider';
import { LazyBarChart, LazyPieChart } from '@/components/LazyCharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface BusinessProfile {
  industry: string;
  companySize: string;
  employeeCount: number;
  avgHourlyCost: number;
  overheadMultiplier: number;
  annualRevenue: number;
  peakHoursPerDay: number;
  peakDaysPerWeek: number;
  criticalSystems: number;
  dataSensitivity: string;
  complianceRequirements: string[];
}

interface CurrentBackupState {
  backupFrequency: string;
  avgRecoveryTime: number; // in hours
  recoverySuccessRate: number;
  outagesLast12Months: number;
  avgOutageDuration: number; // in hours
}

interface RiskTolerance {
  maxAcceptableDowntime: number; // in hours
  maxAcceptableDataLoss: number; // in hours
  slaCommitment: number; // percentage (e.g., 99.9)
}

interface CalculationResults {
  downtimeCost: {
    perHour: number;
    perDay: number;
    perWeek: number;
    perMonth: number;
    perYear: number;
  };
  recommendedRTO: {
    hours: number;
    tier: string;
    tierNumber: number;
    strategy: string;
    estimatedCost: string;
  };
  recommendedRPO: {
    hours: number;
    backupFrequency: string;
    strategy: string;
    hourlyDataValue: number;
    potentialDataLossCost: number;
  };
  strategies: BackupStrategy[];
  architecture: {
    recommended: string;
    technologies: string[];
    storageRecommendation: string;
    threeToOneCompliance: boolean;
  };
}

interface BackupStrategy {
  name: string;
  annualCost: number;
  rto: number;
  rpo: number;
  expectedDowntimeHours: number;
  expectedDataLossHours: number;
  totalRiskCost: number;
  totalCostOfOwnership: number;
  description: string;
  technologies: string[];
  roi?: number;
  savings?: number;
  breakEvenMonths?: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INDUSTRIES = [
  { value: 'healthcare', label: 'Healthcare', criticalityFactor: 0.5 },
  { value: 'finance', label: 'Finance', criticalityFactor: 0.6 },
  { value: 'saas', label: 'SaaS', criticalityFactor: 0.7 },
  { value: 'legal', label: 'Legal', criticalityFactor: 0.8 },
  { value: 'retail', label: 'Retail', criticalityFactor: 0.8 },
  { value: 'manufacturing', label: 'Manufacturing', criticalityFactor: 0.9 },
  { value: 'other', label: 'Other', criticalityFactor: 1.0 },
];

const COMPANY_SIZES = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501+', label: '501+ employees' },
];

const DATA_SENSITIVITY = [
  { value: 'low', label: 'Low', multiplier: 0.5 },
  { value: 'medium', label: 'Medium', multiplier: 1.0 },
  { value: 'high', label: 'High', multiplier: 2.0 },
  { value: 'critical', label: 'Critical', multiplier: 5.0 },
];

const COMPLIANCE_OPTIONS = [
  { value: 'hipaa', label: 'HIPAA', factor: 0.7 },
  { value: 'pci-dss', label: 'PCI-DSS', factor: 0.7 },
  { value: 'soc2', label: 'SOC 2', factor: 0.7 },
  { value: 'gdpr', label: 'GDPR', factor: 0.8 },
  { value: 'none', label: 'None', factor: 1.0 },
];

const BACKUP_FREQUENCIES = [
  { value: 'realtime', label: 'Real-time (Continuous)', hours: 0.016 },
  { value: 'hourly', label: 'Hourly', hours: 1 },
  { value: '4hours', label: 'Every 4 hours', hours: 4 },
  { value: 'daily', label: 'Daily', hours: 24 },
  { value: 'weekly', label: 'Weekly', hours: 168 },
];

const CHART_COLORS = ['#2cc3d1', '#2813e8', '#4B4F58', '#9333ea', '#f59e0b', '#10b981'];

// ============================================================================
// CALCULATION FUNCTIONS
// ============================================================================

function calculateDowntimeCost(profile: BusinessProfile): {
  perHour: number;
  perDay: number;
  perWeek: number;
  perMonth: number;
  perYear: number;
} {
  // Employee cost per hour
  const employeeCostPerHour = profile.employeeCount * profile.avgHourlyCost * (1 + profile.overheadMultiplier / 100);

  // Revenue loss per hour
  const annualWorkingHours = profile.peakHoursPerDay * profile.peakDaysPerWeek * 52;
  const revenueLossPerHour = profile.annualRevenue / annualWorkingHours;

  // Total downtime cost per hour
  const perHour = employeeCostPerHour + revenueLossPerHour;
  const perDay = perHour * profile.peakHoursPerDay;
  const perWeek = perDay * profile.peakDaysPerWeek;
  const perMonth = perWeek * 4.33;
  const perYear = perHour * annualWorkingHours;

  return { perHour, perDay, perWeek, perMonth, perYear };
}

function calculateRecommendedRTO(
  tolerance: RiskTolerance,
  profile: BusinessProfile
): { hours: number; tier: string; tierNumber: number; strategy: string; estimatedCost: string } {
  // Get industry criticality factor
  const industry = INDUSTRIES.find(i => i.value === profile.industry) || INDUSTRIES[INDUSTRIES.length - 1];
  const industryCriticalityFactor = industry.criticalityFactor;

  // Get compliance factor
  const hasStrictCompliance = profile.complianceRequirements.some(c =>
    ['hipaa', 'pci-dss', 'soc2'].includes(c)
  );
  const complianceFactor = hasStrictCompliance ? 0.7 : profile.complianceRequirements.includes('gdpr') ? 0.8 : 1.0;

  // Calculate adjusted RTO
  const adjustedRTO = tolerance.maxAcceptableDowntime * industryCriticalityFactor * complianceFactor;

  // Determine tier
  let tier = '';
  let tierNumber = 4;
  let strategy = '';
  let estimatedCost = '';

  if (adjustedRTO < 1) {
    tier = 'Tier 1 (Mission Critical)';
    tierNumber = 1;
    strategy = 'Real-time replication, hot standby, automated failover';
    estimatedCost = 'High ($$$$$)';
  } else if (adjustedRTO < 4) {
    tier = 'Tier 2 (Business Critical)';
    tierNumber = 2;
    strategy = 'Hourly backups, warm standby, semi-automated recovery';
    estimatedCost = 'Medium-High ($$$$)';
  } else if (adjustedRTO < 24) {
    tier = 'Tier 3 (Important)';
    tierNumber = 3;
    strategy = 'Daily backups, cold standby, manual recovery procedures';
    estimatedCost = 'Medium ($$$)';
  } else {
    tier = 'Tier 4 (Non-Critical)';
    tierNumber = 4;
    strategy = 'Weekly backups, restore from backup';
    estimatedCost = 'Low ($$)';
  }

  return { hours: Math.round(adjustedRTO * 10) / 10, tier, tierNumber, strategy, estimatedCost };
}

function calculateRecommendedRPO(
  tolerance: RiskTolerance,
  profile: BusinessProfile,
  downtimeCost: { perHour: number }
): {
  hours: number;
  backupFrequency: string;
  strategy: string;
  hourlyDataValue: number;
  potentialDataLossCost: number;
} {
  // Get data sensitivity multiplier
  const sensitivity = DATA_SENSITIVITY.find(s => s.value === profile.dataSensitivity) || DATA_SENSITIVITY[1];
  const hourlyDataValue = downtimeCost.perHour * sensitivity.multiplier;

  // Calculate potential data loss cost
  const potentialDataLossCost = hourlyDataValue * tolerance.maxAcceptableDataLoss;

  // Determine backup frequency (RPO / 2 for safety margin)
  const recommendedRPO = tolerance.maxAcceptableDataLoss;
  const targetBackupInterval = recommendedRPO / 2;

  let backupFrequency = '';
  let strategy = '';

  if (targetBackupInterval < 0.25) {
    backupFrequency = 'Continuous (every few minutes)';
    strategy = 'Continuous Data Protection (CDP), real-time replication';
  } else if (targetBackupInterval < 1) {
    backupFrequency = 'Every 15-30 minutes';
    strategy = 'Near-real-time snapshots, high-frequency sync';
  } else if (targetBackupInterval < 4) {
    backupFrequency = 'Hourly';
    strategy = 'Hourly snapshots, incremental backups';
  } else if (targetBackupInterval < 12) {
    backupFrequency = 'Every 4-6 hours';
    strategy = 'Periodic backups, differential backups';
  } else {
    backupFrequency = 'Daily';
    strategy = 'Daily backups, full + incremental';
  }

  return {
    hours: Math.round(recommendedRPO * 10) / 10,
    backupFrequency,
    strategy,
    hourlyDataValue,
    potentialDataLossCost,
  };
}

function generateBackupStrategies(
  downtimeCost: { perHour: number },
  currentBackup: CurrentBackupState,
  profile: BusinessProfile,
  rtoRec: { hours: number; tierNumber: number },
  rpoRec: { hours: number; hourlyDataValue: number }
): BackupStrategy[] {
  const strategies: BackupStrategy[] = [];

  // Current State Strategy
  const currentBackupHours = BACKUP_FREQUENCIES.find(f => f.value === currentBackup.backupFrequency)?.hours || 24;
  const currentExpectedDowntime = currentBackup.outagesLast12Months * currentBackup.avgOutageDuration;
  const currentExpectedDataLoss = currentBackup.outagesLast12Months * (currentBackupHours / 2);
  const currentRiskCost = (currentExpectedDowntime * downtimeCost.perHour) + (currentExpectedDataLoss * rpoRec.hourlyDataValue);

  strategies.push({
    name: 'Current State',
    annualCost: profile.employeeCount * 500, // Estimate
    rto: currentBackup.avgRecoveryTime,
    rpo: currentBackupHours,
    expectedDowntimeHours: currentExpectedDowntime,
    expectedDataLossHours: currentExpectedDataLoss,
    totalRiskCost: currentRiskCost,
    totalCostOfOwnership: (profile.employeeCount * 500) + currentRiskCost,
    description: 'Your current backup configuration',
    technologies: ['Current backup solution'],
  });

  // Recommended (Optimized) Strategy
  const recommendedCost = profile.employeeCount * (800 + (rtoRec.tierNumber === 1 ? 1200 : rtoRec.tierNumber === 2 ? 800 : 400));
  const recommendedDowntime = currentExpectedDowntime * 0.3; // 70% reduction
  const recommendedDataLoss = currentExpectedDataLoss * 0.3; // 70% reduction
  const recommendedRiskCost = (recommendedDowntime * downtimeCost.perHour) + (recommendedDataLoss * rpoRec.hourlyDataValue);

  strategies.push({
    name: 'Recommended (Optimized)',
    annualCost: recommendedCost,
    rto: rtoRec.hours,
    rpo: rpoRec.hours,
    expectedDowntimeHours: recommendedDowntime,
    expectedDataLossHours: recommendedDataLoss,
    totalRiskCost: recommendedRiskCost,
    totalCostOfOwnership: recommendedCost + recommendedRiskCost,
    description: 'Balanced approach optimizing cost and protection',
    technologies: rtoRec.tierNumber === 1
      ? ['Active-Active replication', 'Geo-redundant storage', 'Auto-failover']
      : rtoRec.tierNumber === 2
        ? ['VM replication', 'Hourly snapshots', 'Hot standby']
        : ['Daily incremental backups', 'Image-based backup', 'Cloud storage'],
  });

  // Premium (Maximum Protection) Strategy
  const premiumCost = profile.employeeCount * 2000;
  const premiumDowntime = currentExpectedDowntime * 0.1; // 90% reduction
  const premiumDataLoss = currentExpectedDataLoss * 0.05; // 95% reduction
  const premiumRiskCost = (premiumDowntime * downtimeCost.perHour) + (premiumDataLoss * rpoRec.hourlyDataValue);

  strategies.push({
    name: 'Premium (Maximum Protection)',
    annualCost: premiumCost,
    rto: 0.5,
    rpo: 0.25,
    expectedDowntimeHours: premiumDowntime,
    expectedDataLossHours: premiumDataLoss,
    totalRiskCost: premiumRiskCost,
    totalCostOfOwnership: premiumCost + premiumRiskCost,
    description: 'Enterprise-grade protection with minimal risk',
    technologies: ['Active-Active clustering', 'Continuous replication', 'Zero-data-loss architecture', 'Multi-region failover'],
  });

  // Budget (Minimum Viable) Strategy
  const budgetCost = profile.employeeCount * 300;
  const budgetDowntime = currentExpectedDowntime * 0.6; // 40% reduction
  const budgetDataLoss = currentExpectedDataLoss * 0.7; // 30% reduction
  const budgetRiskCost = (budgetDowntime * downtimeCost.perHour) + (budgetDataLoss * rpoRec.hourlyDataValue);

  strategies.push({
    name: 'Budget (Minimum Viable)',
    annualCost: budgetCost,
    rto: 24,
    rpo: 24,
    expectedDowntimeHours: budgetDowntime,
    expectedDataLossHours: budgetDataLoss,
    totalRiskCost: budgetRiskCost,
    totalCostOfOwnership: budgetCost + budgetRiskCost,
    description: 'Basic protection for non-critical systems',
    technologies: ['Daily backups', 'Cloud backup service', 'Manual recovery'],
  });

  // Calculate ROI for non-current strategies
  const currentStrategy = strategies[0];
  strategies.forEach((strategy, index) => {
    if (index > 0) {
      const additionalInvestment = strategy.annualCost - currentStrategy.annualCost;
      const annualSavings = currentStrategy.totalRiskCost - strategy.totalRiskCost;
      strategy.savings = annualSavings;
      strategy.roi = additionalInvestment > 0 ? (annualSavings / additionalInvestment) * 100 : 0;
      strategy.breakEvenMonths = additionalInvestment > 0 && annualSavings > 0 ? (additionalInvestment / annualSavings) * 12 : 0;
    }
  });

  return strategies;
}

function getArchitectureRecommendation(rto: number, rpo: number) {
  if (rto < 1 && rpo < 0.25) {
    return {
      recommended: 'Active-Active Replication',
      technologies: ['Continuous replication', 'Clustered servers', 'Geo-redundancy', 'Load balancing'],
      storageRecommendation: 'NVMe SSD (hot), Object Storage (warm), Glacier (cold)',
      threeToOneCompliance: true,
    };
  } else if (rto < 4 && rpo < 1) {
    return {
      recommended: 'Active-Passive with Hourly Snapshots',
      technologies: ['VM replication', 'Hourly snapshots', 'Hot standby', 'Automated failover'],
      storageRecommendation: 'SSD (hot), HDD (warm), Cloud backup (cold)',
      threeToOneCompliance: true,
    };
  } else if (rto < 24 && rpo < 4) {
    return {
      recommended: 'Daily Backups with Quick Recovery',
      technologies: ['Daily incremental backups', 'Image-based backup', 'Deduplication', 'Cloud storage'],
      storageRecommendation: 'HDD (hot), Object storage (warm), Archive storage (cold)',
      threeToOneCompliance: true,
    };
  } else {
    return {
      recommended: 'Traditional Backup',
      technologies: ['Weekly full backups', 'Daily incremental', 'Tape or cloud backup', 'Manual recovery'],
      storageRecommendation: 'HDD (primary), Cloud backup (secondary), Tape (archive)',
      threeToOneCompliance: true,
    };
  }
}

// ============================================================================
// PDF EXPORT FUNCTION
// ============================================================================

interface jsPDFWithPlugin extends jsPDF {
  lastAutoTable: {
    finalY: number;
  };
}

function generatePDFReport(
  results: CalculationResults,
  profile: BusinessProfile
) {
  const doc = new jsPDF() as jsPDFWithPlugin;
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Title Page
  doc.setFontSize(20);
  doc.setTextColor(40, 19, 232);
  doc.setFont('helvetica', 'bold');
  doc.text('INVENTIVE HQ', pageWidth / 2, 50, { align: 'center' });

  doc.setFontSize(28);
  doc.setTextColor(17, 24, 39);
  doc.text('Backup Recovery Time', pageWidth / 2, 70, { align: 'center' });
  doc.text('Assessment Report', pageWidth / 2, 82, { align: 'center' });

  doc.setFontSize(14);
  doc.setTextColor(75, 85, 99);
  doc.setFont('helvetica', 'normal');
  doc.text('RTO/RPO Planning & Cost-Benefit Analysis', pageWidth / 2, 98, { align: 'center' });

  // Report Details
  let yPos = 120;
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(30, yPos, pageWidth - 60, 80, 3, 3, 'F');
  yPos += 12;

  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Organization:', 40, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(`${INDUSTRIES.find(i => i.value === profile.industry)?.label || profile.industry} Industry`, 100, yPos);
  yPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Company Size:', 40, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(`${profile.employeeCount} employees`, 100, yPos);
  yPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Annual Revenue:', 40, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(profile.annualRevenue), 100, yPos);
  yPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Report Date:', 40, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(new Date().toLocaleDateString(), 100, yPos);
  yPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Downtime Cost:', 40, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(`${formatCurrency(results.downtimeCost.perHour)} per hour`, 100, yPos);
  yPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Data Loss Cost:', 40, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(`${formatCurrency(results.recommendedRPO.hourlyDataValue)} per hour of data`, 100, yPos);

  // Key Recommendations
  yPos += 25;
  doc.setDrawColor(44, 195, 209);
  doc.setLineWidth(2);
  doc.roundedRect(30, yPos, pageWidth - 60, 50, 3, 3, 'S');
  yPos += 12;

  doc.setFontSize(12);
  doc.setTextColor(44, 195, 209);
  doc.setFont('helvetica', 'bold');
  doc.text('Recommended RTO (Recovery Time Objective)', pageWidth / 2, yPos, { align: 'center' });
  yPos += 8;
  doc.setFontSize(20);
  doc.text(`${results.recommendedRTO.hours} hours`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 6;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(results.recommendedRTO.tier, pageWidth / 2, yPos, { align: 'center' });

  yPos += 12;
  doc.setFontSize(12);
  doc.setTextColor(44, 195, 209);
  doc.setFont('helvetica', 'bold');
  doc.text('Recommended RPO (Recovery Point Objective)', pageWidth / 2, yPos, { align: 'center' });
  yPos += 8;
  doc.setFontSize(20);
  doc.text(`${results.recommendedRPO.hours} hours`, pageWidth / 2, yPos, { align: 'center' });

  // Footer
  const footerY = pageHeight - 20;
  doc.setFontSize(8);
  doc.setTextColor(156, 163, 175);
  doc.text('inventivehq.com | (866) 903-2097 | info@inventivehq.com', pageWidth / 2, footerY, { align: 'center' });

  // Page 2: Strategy Comparison
  doc.addPage();
  yPos = 20;

  doc.setFontSize(20);
  doc.setTextColor(40, 19, 232);
  doc.setFont('helvetica', 'bold');
  doc.text('Backup Strategy Comparison', 20, yPos);
  yPos += 15;

  autoTable(doc, {
    startY: yPos,
    head: [['Strategy', 'Annual Cost', 'RTO', 'RPO', 'Total Risk Cost', 'TCO', 'ROI']],
    body: results.strategies.map(s => [
      s.name,
      formatCurrency(s.annualCost),
      `${s.rto}h`,
      `${s.rpo}h`,
      formatCurrency(s.totalRiskCost),
      formatCurrency(s.totalCostOfOwnership),
      s.roi ? `${s.roi.toFixed(0)}%` : '-',
    ]),
    theme: 'grid',
    headStyles: { fillColor: [44, 195, 209], textColor: 255, fontStyle: 'bold', fontSize: 9 },
    styles: { fontSize: 8, cellPadding: 2 },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 25, halign: 'right' },
      2: { cellWidth: 15, halign: 'center' },
      3: { cellWidth: 15, halign: 'center' },
      4: { cellWidth: 28, halign: 'right' },
      5: { cellWidth: 28, halign: 'right' },
      6: { cellWidth: 20, halign: 'right' },
    },
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // Architecture Recommendation
  doc.setFontSize(16);
  doc.setTextColor(40, 19, 232);
  doc.setFont('helvetica', 'bold');
  doc.text('Recommended Architecture', 20, yPos);
  yPos += 10;

  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Solution:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(results.architecture.recommended, 60, yPos);
  yPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Technologies:', 20, yPos);
  yPos += 6;
  doc.setFont('helvetica', 'normal');
  results.architecture.technologies.forEach((tech) => {
    doc.text(`• ${tech}`, 25, yPos);
    yPos += 6;
  });

  yPos += 5;
  doc.setFont('helvetica', 'bold');
  doc.text('Storage Strategy:', 20, yPos);
  yPos += 6;
  doc.setFont('helvetica', 'normal');
  doc.text(results.architecture.storageRecommendation, 25, yPos);
  yPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('3-2-1 Backup Rule Compliant:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(results.architecture.threeToOneCompliance ? 'Yes ✓' : 'No', 80, yPos);

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(156, 163, 175);
  doc.text('inventivehq.com | (866) 903-2097 | info@inventivehq.com', pageWidth / 2, pageHeight - 20, { align: 'center' });

  doc.save('backup-recovery-time-assessment.pdf');
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function BackupRecoveryCalculatorClient() {
  const [step, setStep] = useState(1);

  // Business Profile State
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile>({
    industry: 'other',
    companySize: '11-50',
    employeeCount: 25,
    avgHourlyCost: 35,
    overheadMultiplier: 50,
    annualRevenue: 2500000,
    peakHoursPerDay: 8,
    peakDaysPerWeek: 5,
    criticalSystems: 3,
    dataSensitivity: 'medium',
    complianceRequirements: [],
  });

  // Current Backup State
  const [currentBackup, setCurrentBackup] = useState<CurrentBackupState>({
    backupFrequency: 'daily',
    avgRecoveryTime: 8,
    recoverySuccessRate: 95,
    outagesLast12Months: 2,
    avgOutageDuration: 4,
  });

  // Risk Tolerance State
  const [riskTolerance, setRiskTolerance] = useState<RiskTolerance>({
    maxAcceptableDowntime: 8,
    maxAcceptableDataLoss: 8,
    slaCommitment: 99.5,
  });

  // Results State
  const [results, setResults] = useState<CalculationResults | null>(null);

  // Calculate Results
  const calculateResults = () => {
    const downtimeCost = calculateDowntimeCost(businessProfile);
    const recommendedRTO = calculateRecommendedRTO(riskTolerance, businessProfile);
    const recommendedRPO = calculateRecommendedRPO(riskTolerance, businessProfile, downtimeCost);
    const strategies = generateBackupStrategies(downtimeCost, currentBackup, businessProfile, recommendedRTO, recommendedRPO);
    const architecture = getArchitectureRecommendation(recommendedRTO.hours, recommendedRPO.hours);

    const calculatedResults: CalculationResults = {
      downtimeCost,
      recommendedRTO,
      recommendedRPO,
      strategies,
      architecture,
    };

    setResults(calculatedResults);
    setStep(4);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= s
                    ? 'bg-[#2cc3d1] text-white'
                    : 'bg-gray-200 text-gray-500'
                  }`}
              >
                {s}
              </div>
              {s < 4 && (
                <div
                  className={`flex-1 h-1 mx-2 ${step > s ? 'bg-[#2cc3d1]' : 'bg-gray-200'
                    }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2">
          <span className="text-xs font-semibold text-gray-600">Business Profile</span>
          <span className="text-xs font-semibold text-gray-600">Current State</span>
          <span className="text-xs font-semibold text-gray-600">Risk Tolerance</span>
          <span className="text-xs font-semibold text-gray-600">Results</span>
        </div>
      </div>

      {/* Step 1: Business Profile */}
      {step === 1 && (
        <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-gray-200">
          <h2 className="text-2xl font-bold text-[#4B4F58] mb-6">Step 1: Business Profile</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Industry */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Industry
              </label>
              <select
                value={businessProfile.industry}
                onChange={(e) => setBusinessProfile({ ...businessProfile, industry: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2cc3d1] focus:border-transparent"
              >
                {INDUSTRIES.map((ind) => (
                  <option key={ind.value} value={ind.value}>
                    {ind.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Company Size */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Company Size
              </label>
              <select
                value={businessProfile.companySize}
                onChange={(e) => setBusinessProfile({ ...businessProfile, companySize: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2cc3d1] focus:border-transparent"
              >
                {COMPANY_SIZES.map((size) => (
                  <option key={size.value} value={size.value}>
                    {size.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Employee Count */}
            <DualInputSlider
              id="business-employee-count"
              label="Number of Employees"
              value={businessProfile.employeeCount}
              onChange={(val) =>
                setBusinessProfile({ ...businessProfile, employeeCount: Math.round(val) })
              }
              min={1}
              max={10000}
              step={1}
            />

            {/* Average Hourly Cost */}
            <DualInputSlider
              id="business-avg-hourly-cost"
              label="Average Hourly Employee Cost"
              value={businessProfile.avgHourlyCost}
              onChange={(val) =>
                setBusinessProfile({
                  ...businessProfile,
                  avgHourlyCost: Number(val.toFixed(2)),
                })
              }
              min={0}
              max={500}
              step={0.01}
              prefix="$"
              suffix="/hr"
              helpText="Default: $35/hour"
            />

            {/* Overhead Multiplier */}
            <DualInputSlider
              id="business-overhead-multiplier"
              label="Overhead Multiplier"
              value={businessProfile.overheadMultiplier}
              onChange={(val) =>
                setBusinessProfile({
                  ...businessProfile,
                  overheadMultiplier: Math.round(val),
                })
              }
              min={0}
              max={200}
              step={1}
              suffix="%"
              helpText="Typical: 50%"
            />

            {/* Annual Revenue */}
            <DualInputSlider
              id="business-annual-revenue"
              label="Annual Revenue"
              value={businessProfile.annualRevenue}
              onChange={(val) =>
                setBusinessProfile({
                  ...businessProfile,
                  annualRevenue: Math.round(val),
                })
              }
              min={0}
              max={10000000000}
              step={1000}
              formatValue={(val) => formatCurrency(Math.round(val))}
              minLabel={formatCurrency(0)}
              maxLabel={formatCurrency(10000000000)}
            />

            {/* Peak Hours Per Day */}
            <DualInputSlider
              id="business-peak-hours"
              label="Peak Business Hours Per Day"
              value={businessProfile.peakHoursPerDay}
              onChange={(val) =>
                setBusinessProfile({
                  ...businessProfile,
                  peakHoursPerDay: Math.round(val),
                })
              }
              min={1}
              max={24}
              step={1}
              suffix="hrs"
              helpText="Default: 8 hours"
            />

            {/* Peak Days Per Week */}
            <DualInputSlider
              id="business-peak-days"
              label="Peak Business Days Per Week"
              value={businessProfile.peakDaysPerWeek}
              onChange={(val) =>
                setBusinessProfile({
                  ...businessProfile,
                  peakDaysPerWeek: Math.round(val),
                })
              }
              min={1}
              max={7}
              step={1}
              suffix="days"
              helpText="Default: 5 days"
            />

            {/* Critical Systems */}
            <DualInputSlider
              id="business-critical-systems"
              label="Number of Critical Systems"
              value={businessProfile.criticalSystems}
              onChange={(val) =>
                setBusinessProfile({
                  ...businessProfile,
                  criticalSystems: Math.round(val),
                })
              }
              min={1}
              max={200}
              step={1}
            />

            {/* Data Sensitivity */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Data Sensitivity Level
              </label>
              <select
                value={businessProfile.dataSensitivity}
                onChange={(e) => setBusinessProfile({ ...businessProfile, dataSensitivity: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2cc3d1] focus:border-transparent"
              >
                {DATA_SENSITIVITY.map((sens) => (
                  <option key={sens.value} value={sens.value}>
                    {sens.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Compliance Requirements */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Compliance Requirements (select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {COMPLIANCE_OPTIONS.filter(c => c.value !== 'none').map((comp) => (
                  <label key={comp.value} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={businessProfile.complianceRequirements.includes(comp.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setBusinessProfile({
                            ...businessProfile,
                            complianceRequirements: [...businessProfile.complianceRequirements, comp.value],
                          });
                        } else {
                          setBusinessProfile({
                            ...businessProfile,
                            complianceRequirements: businessProfile.complianceRequirements.filter(c => c !== comp.value),
                          });
                        }
                      }}
                      className="rounded border-gray-300 text-[#2cc3d1] focus:ring-[#2cc3d1]"
                    />
                    <span className="text-sm">{comp.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={() => setStep(2)}
              className="px-8 py-3 bg-[#2cc3d1] text-white font-semibold rounded-lg hover:bg-[#2813e8] transition-colors"
            >
              Next: Current Backup State
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Current Backup State */}
      {step === 2 && (
        <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-gray-200">
          <h2 className="text-2xl font-bold text-[#4B4F58] mb-6">Step 2: Current Backup State</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Backup Frequency */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Current Backup Frequency
              </label>
              <select
                value={currentBackup.backupFrequency}
                onChange={(e) => setCurrentBackup({ ...currentBackup, backupFrequency: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2cc3d1] focus:border-transparent"
              >
                {BACKUP_FREQUENCIES.map((freq) => (
                  <option key={freq.value} value={freq.value}>
                    {freq.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Average Recovery Time */}
            <DualInputSlider
              id="current-avg-recovery-time"
              label="Average Recovery Time"
              value={currentBackup.avgRecoveryTime}
              onChange={(val) =>
                setCurrentBackup({
                  ...currentBackup,
                  avgRecoveryTime: Number(val.toFixed(1)),
                })
              }
              min={0}
              max={168}
              step={0.5}
              suffix="hrs"
            />

            {/* Recovery Success Rate */}
            <DualInputSlider
              id="current-recovery-success-rate"
              label="Recovery Success Rate"
              value={currentBackup.recoverySuccessRate}
              onChange={(val) =>
                setCurrentBackup({
                  ...currentBackup,
                  recoverySuccessRate: Number(val.toFixed(1)),
                })
              }
              min={0}
              max={100}
              step={0.5}
              suffix="%"
              helpText="Default: 95%"
            />

            {/* Outages Last 12 Months */}
            <DualInputSlider
              id="current-outages"
              label="Outages in Last 12 Months"
              value={currentBackup.outagesLast12Months}
              onChange={(val) =>
                setCurrentBackup({
                  ...currentBackup,
                  outagesLast12Months: Math.round(val),
                })
              }
              min={0}
              max={50}
              step={1}
            />

            {/* Average Outage Duration */}
            <DualInputSlider
              id="current-avg-outage-duration"
              label="Average Outage Duration"
              value={currentBackup.avgOutageDuration}
              onChange={(val) =>
                setCurrentBackup({
                  ...currentBackup,
                  avgOutageDuration: Number(val.toFixed(1)),
                })
              }
              min={0}
              max={168}
              step={0.5}
              suffix="hrs"
            />
          </div>

          <div className="mt-8 flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="px-8 py-3 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              className="px-8 py-3 bg-[#2cc3d1] text-white font-semibold rounded-lg hover:bg-[#2813e8] transition-colors"
            >
              Next: Risk Tolerance
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Risk Tolerance */}
      {step === 3 && (
        <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-gray-200">
          <h2 className="text-2xl font-bold text-[#4B4F58] mb-6">Step 3: Risk Tolerance</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Max Acceptable Downtime */}
            <DualInputSlider
              id="risk-max-downtime"
              label="Maximum Acceptable Downtime"
              value={riskTolerance.maxAcceptableDowntime}
              onChange={(val) =>
                setRiskTolerance({
                  ...riskTolerance,
                  maxAcceptableDowntime: Number(val.toFixed(1)),
                })
              }
              min={0}
              max={168}
              step={0.5}
              suffix="hrs"
              helpText="This is your RTO target before adjustments."
            />
            <p className="text-xs text-gray-500 -mt-4 mb-4">
              Need to convert between time units? Use our <Link href="/tools/planning/time-duration-calculator" className="text-blue-600 hover:text-blue-800 underline">Time Duration Calculator</Link>.
            </p>

            {/* Max Acceptable Data Loss */}
            <DualInputSlider
              id="risk-max-data-loss"
              label="Maximum Acceptable Data Loss"
              value={riskTolerance.maxAcceptableDataLoss}
              onChange={(val) =>
                setRiskTolerance({
                  ...riskTolerance,
                  maxAcceptableDataLoss: Number(val.toFixed(1)),
                })
              }
              min={0}
              max={168}
              step={0.5}
              suffix="hrs"
              helpText="This is your RPO target"
            />

            {/* SLA Commitment */}
            <DualInputSlider
              id="risk-sla-commitment"
              label="Customer SLA Commitment (uptime)"
              value={riskTolerance.slaCommitment}
              onChange={(val) =>
                setRiskTolerance({
                  ...riskTolerance,
                  slaCommitment: Number(val.toFixed(1)),
                })
              }
              min={0}
              max={100}
              step={0.1}
              suffix="%"
              helpText="E.g., 99.9% = 8.76 hours downtime/year"
            />
          </div>

          <div className="mt-8 flex justify-between">
            <button
              onClick={() => setStep(2)}
              className="px-8 py-3 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors"
            >
              Back
            </button>
            <button
              onClick={calculateResults}
              className="px-8 py-3 bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              Calculate Results
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Results */}
      {step === 4 && results && (
        <div className="space-y-8">
          {/* Executive Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-[#2cc3d1] to-[#2813e8] text-white rounded-xl p-6 shadow-lg">
              <h3 className="text-sm font-semibold uppercase tracking-wide opacity-90 mb-2">
                Downtime Cost
              </h3>
              <p className="text-3xl font-bold">{formatCurrency(results.downtimeCost.perHour)}</p>
              <p className="text-sm opacity-90 mt-1">per hour</p>
              <p className="text-lg mt-2">{formatCurrency(results.downtimeCost.perDay)}/day</p>
            </div>

            <div className="bg-gradient-to-br from-[#4B4F58] to-[#2813e8] text-white rounded-xl p-6 shadow-lg">
              <h3 className="text-sm font-semibold uppercase tracking-wide opacity-90 mb-2">
                Recommended RTO
              </h3>
              <p className="text-3xl font-bold">{results.recommendedRTO.hours} hours</p>
              <p className="text-sm opacity-90 mt-1">{results.recommendedRTO.tier}</p>
            </div>

            <div className="bg-gradient-to-br from-[#9333ea] to-[#2813e8] text-white rounded-xl p-6 shadow-lg">
              <h3 className="text-sm font-semibold uppercase tracking-wide opacity-90 mb-2">
                Recommended RPO
              </h3>
              <p className="text-3xl font-bold">{results.recommendedRPO.hours} hours</p>
              <p className="text-sm opacity-90 mt-1">{results.recommendedRPO.backupFrequency}</p>
            </div>

            <div className="bg-gradient-to-br from-[#10b981] to-[#2813e8] text-white rounded-xl p-6 shadow-lg">
              <h3 className="text-sm font-semibold uppercase tracking-wide opacity-90 mb-2">
                Potential Savings
              </h3>
              <p className="text-3xl font-bold">
                {results.strategies[1]?.savings ? formatCurrency(results.strategies[1].savings) : '-'}
              </p>
              <p className="text-sm opacity-90 mt-1">per year</p>
              <p className="text-lg mt-2">
                {results.strategies[1]?.roi ? `${results.strategies[1].roi.toFixed(0)}% ROI` : '-'}
              </p>
            </div>
          </div>

          {/* Downtime Cost Breakdown */}
          <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-gray-200">
            <h3 className="text-2xl font-bold text-[#4B4F58] mb-6">Downtime Cost Breakdown</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <LazyPieChart
                  data={[
                    {
                      name: 'Employee Costs',
                      value: businessProfile.employeeCount * businessProfile.avgHourlyCost * (1 + businessProfile.overheadMultiplier / 100),
                    },
                    {
                      name: 'Revenue Loss',
                      value: businessProfile.annualRevenue / (businessProfile.peakHoursPerDay * businessProfile.peakDaysPerWeek * 52),
                    },
                  ]}
                  dataKey="value"
                  nameKey="name"
                  colors={CHART_COLORS.slice(0, 2)}
                  height={300}
                  outerRadius={100}
                  showLabel={true}
                  labelFormatter={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                />
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-600">Per Hour</p>
                  <p className="text-2xl font-bold text-[#2cc3d1]">{formatCurrency(results.downtimeCost.perHour)}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Per Day</p>
                  <p className="text-xl font-bold text-gray-700">{formatCurrency(results.downtimeCost.perDay)}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Per Week</p>
                  <p className="text-xl font-bold text-gray-700">{formatCurrency(results.downtimeCost.perWeek)}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Per Month</p>
                  <p className="text-xl font-bold text-gray-700">{formatCurrency(results.downtimeCost.perMonth)}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Per Year</p>
                  <p className="text-xl font-bold text-gray-700">{formatCurrency(results.downtimeCost.perYear)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Strategy Comparison Table */}
          <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-gray-200">
            <h3 className="text-2xl font-bold text-[#4B4F58] mb-6">Backup Strategy Comparison</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] text-white">
                    <th className="px-4 py-3 text-left font-semibold">Strategy</th>
                    <th className="px-4 py-3 text-right font-semibold">Annual Cost</th>
                    <th className="px-4 py-3 text-center font-semibold">RTO</th>
                    <th className="px-4 py-3 text-center font-semibold">RPO</th>
                    <th className="px-4 py-3 text-right font-semibold">Risk Cost</th>
                    <th className="px-4 py-3 text-right font-semibold">Total TCO</th>
                    <th className="px-4 py-3 text-right font-semibold">ROI</th>
                  </tr>
                </thead>
                <tbody>
                  {results.strategies.map((strategy, index) => (
                    <tr
                      key={strategy.name}
                      className={`border-b ${index === 1 ? 'bg-blue-50 font-semibold' : ''
                        }`}
                    >
                      <td className="px-4 py-3">{strategy.name}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(strategy.annualCost)}</td>
                      <td className="px-4 py-3 text-center">{strategy.rto}h</td>
                      <td className="px-4 py-3 text-center">{strategy.rpo}h</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(strategy.totalRiskCost)}</td>
                      <td className="px-4 py-3 text-right font-bold">{formatCurrency(strategy.totalCostOfOwnership)}</td>
                      <td className="px-4 py-3 text-right">
                        {strategy.roi ? (
                          <span className={strategy.roi > 100 ? 'text-green-600 font-bold' : 'text-gray-600'}>
                            {strategy.roi.toFixed(0)}%
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Strategy Comparison Chart */}
          <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-gray-200">
            <h3 className="text-2xl font-bold text-[#4B4F58] mb-6">Total Cost of Ownership Comparison</h3>
            <LazyBarChart
              data={results.strategies.map(s => ({
                name: s.name,
                annualCost: s.annualCost,
                totalRiskCost: s.totalRiskCost,
              } as const)).map(item => item as { [key: string]: string | number })}
              xAxisKey="name"
              bars={[
                { dataKey: 'annualCost', name: 'Annual Backup Cost', fill: CHART_COLORS[0] },
                { dataKey: 'totalRiskCost', name: 'Annual Risk Cost', fill: CHART_COLORS[1] },
              ]}
              height={400}
              yAxisFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
              tooltipFormatter={(value) => formatCurrency(value)}
            />
          </div>

          {/* Architecture Recommendation */}
          <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-gray-200">
            <h3 className="text-2xl font-bold text-[#4B4F58] mb-6">Recommended Architecture</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-semibold text-gray-700 mb-3">Solution Type</h4>
                <p className="text-xl font-bold text-[#2cc3d1] mb-4">{results.architecture.recommended}</p>

                <h4 className="text-lg font-semibold text-gray-700 mb-3">Technologies</h4>
                <ul className="space-y-2">
                  {results.architecture.technologies.map((tech, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-[#2cc3d1] mr-2">✓</span>
                      <span>{tech}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-gray-700 mb-3">Storage Strategy</h4>
                <p className="text-gray-700 mb-4">{results.architecture.storageRecommendation}</p>

                <h4 className="text-lg font-semibold text-gray-700 mb-3">3-2-1 Backup Rule</h4>
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                  <p className="font-semibold text-green-800 mb-2">
                    {results.architecture.threeToOneCompliance ? '✓ Compliant' : '✗ Not Compliant'}
                  </p>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• 3 copies of your data</li>
                    <li>• 2 different storage media types</li>
                    <li>• 1 offsite or cloud backup</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => generatePDFReport(results, businessProfile)}
              className="px-8 py-4 bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              Export PDF Report
            </button>
            <button
              onClick={() => {
                setStep(1);
                setResults(null);
              }}
              className="px-8 py-4 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors"
            >
              Start New Calculation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
